import { useState } from 'react';
import Countdown from 'react-countdown';
import { useTransition, animated, config } from 'react-spring';
import classNames from 'classnames/bind';
import MetaDescription from 'components/Metatags/MetaDescription';
import MetaTitle from 'components/Metatags/MetaTitle';
import Page from 'layouts/Page/Page';
import Cover from 'components/v2/Cover/Cover';
import { Text } from 'design-system';
import { Grid } from 'forkedlibraries/react-bootstrap';
import Button, { BUTTON_SIZES } from 'components/Button/Button';
import { Button as UIButton } from '@belong/ui';
import { THEME_CONSTS } from 'themes/themes';
import { useInterval } from 'hooks/useInterval';
import { useWindowSize } from 'hooks/useWindowSize';
import styles from './landing.module.css';

const cx = classNames.bind(styles);

const fadeAnimationConfig = {
  from: { opacity: 0 },
  enter: { opacity: 1 },
  leave: { opacity: 0 },
  expires: false,
  config: config.molasses,
};

/**
 * What the GetStarted step's payload carries. Exported so the flow can state that
 * the step it hands over is this one — its own merged type cannot, because the
 * payload half arrives untyped through `dispatch`.
 */
export type LandingCurrentStep = {
  id: string;
  stepName: string;
  status: string;
  address: string;
  bannerImgUrl: string;
  isPrimary: boolean;
  isRoommate: boolean;
  isCosigner: boolean;
  isExpired: boolean;
  leaseStartOn: string;
  offerExpiresOn: string;
  daysUntilStart: number;
  isRenewal: boolean;
  isSelfServe: boolean;
};

interface LandingProps {
  onStartSigning: () => void;
  currentStep: LandingCurrentStep;
}

export const Landing = ({ currentStep, onStartSigning }: LandingProps) => {
  useWindowSize();

  const [current, setCurrent] = useState(0);

  // Map currentStep data to slides format
  const slides = [
    {
      src: currentStep.bannerImgUrl,
      title: currentStep.isRenewal
        ? 'Welcome back! Ready to renew your lease?'
        : 'Your new Home awaits',
    },
  ];

  const slidesCount = slides.length;

  useInterval(() => {
    if (slidesCount > 1) setCurrent((i) => (i + 1) % slidesCount);
  }, 5000);

  // `slides` is a literal with at least one entry and the index is clamped to
  // its last, so there is always a slide here.
  const currentSlide = slides[Math.min(current, slidesCount - 1)]!;
  const sectionTransitions = useTransition(currentSlide, fadeAnimationConfig);

  // Determine if we should show countdown
  const showCountdown = !currentStep.isExpired && currentStep.isPrimary;

  // Determine CTA configuration
  const getCTA = () => {
    if (currentStep.isExpired) {
      return {
        kind: 'expired',
        label: 'Go to Home',
        disabled: false,
        href: '/home',
      };
    }

    return {
      kind: 'start_signing',
      label: currentStep.isRenewal ? 'Renew my lease' : 'Start my lease',
      disabled: false,
    };
  };

  const cta = getCTA();

  // Generate meta information
  const metaTitle = `Lease signing · ${currentStep.address}`;
  const metaDescription = currentStep.isRenewal
    ? 'Renew your lease and continue enjoying your home.'
    : 'Sign your lease and secure your new home.';

  return (
    <Page
      header={true}
      headerMainProps={{
        borderOnScroll: false,
        elevated: true,
        hasborder: false,
        theme: THEME_CONSTS.DARK,
        transparent: true,
      }}
    >
      <MetaTitle title={metaTitle} />
      <MetaDescription description={metaDescription} />
      <div
        className={cx('content-layout')}
        style={{
          minHeight: `calc(100dvh)`,
        }}
      >
        <div
          className={cx('cover-container')}
          style={{ flex: 1, minHeight: 0 }}
        >
          {sectionTransitions((style, item) => (
            <animated.div
              className={cx('fade-container')}
              style={{ ...style, height: '100%' }}
            >
              <Cover
                className={cx('cover-image', {
                  'slideshow-animation': !currentStep.isExpired,
                })}
                containerClassName={cx('cover')}
                overlay={<div className={cx('cover-overlay')} />}
                opacity={0.3}
                src={item?.src}
              >
                <div className={cx('cover-content')}>
                  <Text
                    color="white"
                    fontSize="h1"
                    fontWeight="semibold"
                    textAlign="center"
                  >
                    {item?.title}
                  </Text>
                </div>
              </Cover>
            </animated.div>
          ))}
        </div>

        <div className={cx('footer-container')}>
          <Grid>
            <div className={cx('footer')}>
              {showCountdown ? (
                <div className={cx('countdown-container')}>
                  <Countdown
                    date={new Date(currentStep.offerExpiresOn)}
                    renderer={({ hours, minutes, seconds }) => (
                      <Text color="white" fontWeight="semibold" ml="xs">
                        {`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
                          seconds,
                        ).padStart(2, '0')}`}
                      </Text>
                    )}
                  />
                </div>
              ) : (
                <div className="flex-1" />
              )}

              <div className={cx('button-container')}>
                {cta.kind === 'go_home' && (
                  <Button
                    size={BUTTON_SIZES.FLUID_LONG_RESPONSIVE}
                    to={cta.href!}
                    disabled={cta.disabled}
                  >
                    {cta.label}
                  </Button>
                )}
                {cta.kind === 'start_signing' && (
                  <Button
                    size={BUTTON_SIZES.FLUID_LONG_RESPONSIVE}
                    onClick={onStartSigning}
                    disabled={cta.disabled}
                  >
                    {cta.label}
                  </Button>
                )}
                {cta.kind === 'disabled_waiting_primary' && (
                  <UIButton
                    size={{ _: 'fluid', md: 'auto' }}
                    className="px-md"
                    disabled
                  >
                    {cta.label}
                  </UIButton>
                )}
                {cta.kind === 'expired' && (
                  <Button
                    size={BUTTON_SIZES.FLUID_LONG_RESPONSIVE}
                    to={cta.href ?? '/home'}
                  >
                    {cta.label}
                  </Button>
                )}
              </div>
            </div>
          </Grid>
        </div>
      </div>
    </Page>
  );
};
